import { createSlice } from "@reduxjs/toolkit";
import { API_RESULT } from "../../../utils/enums";
import {
  getPlaceAutosuggest,
  flightSearch,
  pollFlightSearch,
  Airport,
  FlightSearchRequest,
  FlightSearchResults,
  FlightSearchResultsWrapper,
  SkyscannerResponseCode,
  normalizeSkyscannerReturnCode,
} from "./FlightsApi";

function recordSize(record: Record<string, unknown> | undefined | null): number {
  return record ? Object.keys(record).length : 0;
}

/** Keep visible results during poll-search when the API returns an empty or partial payload. */
function mergePollSearchResults(
  previous: FlightSearchResults | undefined,
  incoming: FlightSearchResults
): FlightSearchResults {
  if (!previous) return incoming;

  const incomingItineraryCount = recordSize(incoming.itineraries);
  const previousItineraryCount = recordSize(previous.itineraries);

  if (incomingItineraryCount === 0 && previousItineraryCount > 0) {
    return {
      ...incoming,
      itineraries: previous.itineraries,
      legs: { ...previous.legs, ...incoming.legs },
      segments: { ...previous.segments, ...incoming.segments },
      places: { ...previous.places, ...incoming.places },
      carriers: { ...previous.carriers, ...incoming.carriers },
      agents: { ...previous.agents, ...incoming.agents },
      alliances: { ...previous.alliances, ...incoming.alliances },
      fareAttributeFilters: {
        ...previous.fareAttributeFilters,
        ...incoming.fareAttributeFilters,
      },
    };
  }

  return {
    itineraries: { ...previous.itineraries, ...incoming.itineraries },
    legs: { ...previous.legs, ...incoming.legs },
    segments: { ...previous.segments, ...incoming.segments },
    places: { ...previous.places, ...incoming.places },
    carriers: { ...previous.carriers, ...incoming.carriers },
    agents: { ...incoming.agents, ...previous.agents },
    alliances: { ...previous.alliances, ...incoming.alliances },
    fareAttributeFilters: {
      ...previous.fareAttributeFilters,
      ...incoming.fareAttributeFilters,
    },
  };
}

function hasSortingOptions(
  sortingOptions: FlightSearchResultsWrapper["sortingOptions"] | undefined
): boolean {
  if (!sortingOptions) return false;
  return (
    (sortingOptions.best?.length ?? 0) > 0 ||
    (sortingOptions.cheapest?.length ?? 0) > 0 ||
    (sortingOptions.fastest?.length ?? 0) > 0
  );
}

interface FlightsState {
  places: Airport[];
  autosuggestStatus: API_RESULT;
  flightSearchStatus: API_RESULT;
  flightSearchResults: FlightSearchResultsWrapper | null;
  flightSearchReturnCode: SkyscannerResponseCode | null;
  /**
   * Incremented only when POST `/cmd/flights/flight-search` completes successfully
   * (not when `/cmd/flights/poll-search` updates results).
   */
  flightSearchHttpResponseGeneration: number;
  /** Bumped when poll-search fails so polling useEffect can schedule another attempt without clearing results. */
  pollIgnoreErrorNonce: number;
  /** Number of in-flight/completed poll-search requests in the current search session (resets on new flight-search). */
  pollSearchRequestCount: number;
  pollSearchResponseCount: number;
  /** Adults + children from the last successful `flightSearch` request (for per-booking totals in UI). */
  lastSearchPassengerCount: number | null;
  /** Last `flightSearch` body (kept through poll-search) for outbound click payloads. */
  lastFlightSearchRequest: FlightSearchRequest | null;
  /** Display text the user selected for origin (e.g. "תל אביב-יפו, ישראל (TLV)") */
  lastSearchOriginDisplayText: string | null;
  /** Display text the user selected for destination */
  lastSearchDestinationDisplayText: string | null;
}

const initialState: FlightsState = {
  places: [],
  autosuggestStatus: API_RESULT.IDLE,
  flightSearchStatus: API_RESULT.IDLE,
  flightSearchResults: null,
  flightSearchReturnCode: null,
  flightSearchHttpResponseGeneration: 0,
  pollIgnoreErrorNonce: 0,
  pollSearchRequestCount: 0,
  pollSearchResponseCount: 0,
  lastSearchPassengerCount: null,
  lastFlightSearchRequest: null,
  lastSearchOriginDisplayText: null,
  lastSearchDestinationDisplayText: null,
};

const flightsSlice = createSlice({
  name: "flights",
  initialState,
  reducers: {
    clearPlaces: (state) => {
      state.places = [];
      state.autosuggestStatus = API_RESULT.IDLE;
    },
    clearFlightSearch: (state) => {
      state.flightSearchStatus = API_RESULT.IDLE;
      state.flightSearchResults = null;
      state.flightSearchReturnCode = null;
      state.flightSearchHttpResponseGeneration = 0;
      state.pollIgnoreErrorNonce = 0;
      state.pollSearchRequestCount = 0;
      state.pollSearchResponseCount = 0;
      state.lastSearchPassengerCount = null;
      state.lastFlightSearchRequest = null;
      state.lastSearchOriginDisplayText = null;
      state.lastSearchDestinationDisplayText = null;
    },
    setSearchDisplayTexts: (state, action: { payload: { origin: string; destination: string } }) => {
      state.lastSearchOriginDisplayText = action.payload.origin;
      state.lastSearchDestinationDisplayText = action.payload.destination;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getPlaceAutosuggest.pending, (state) => {
        state.autosuggestStatus = API_RESULT.LOADING;
      })
      .addCase(getPlaceAutosuggest.fulfilled, (state, action) => {
        state.places = action.payload.places;
        state.autosuggestStatus = API_RESULT.SUCCEEDED;
      })
      .addCase(getPlaceAutosuggest.rejected, (state) => {
        state.places = [];
        state.autosuggestStatus = API_RESULT.FAILED;
      })
      .addCase(flightSearch.pending, (state) => {
        state.flightSearchStatus = API_RESULT.LOADING;
        state.flightSearchResults = null;
        state.flightSearchReturnCode = null;
        state.pollIgnoreErrorNonce = 0;
        state.pollSearchRequestCount = 0;
        state.pollSearchResponseCount = 0;
        state.lastSearchPassengerCount = null;
        state.lastFlightSearchRequest = null;
      })
      .addCase(flightSearch.fulfilled, (state, action) => {
        state.flightSearchStatus = API_RESULT.SUCCEEDED;
        state.flightSearchHttpResponseGeneration += 1;
        const wrapper = action.payload.results;
        const mergedClickId =
          action.payload.feClickId ?? wrapper.feClickId;
        state.flightSearchResults = {
          ...wrapper,
          ...(mergedClickId != null && mergedClickId !== ""
            ? { feClickId: mergedClickId }
            : {}),
        };
        state.flightSearchReturnCode =
          normalizeSkyscannerReturnCode(action.payload.returnCode);
        const req = action.meta.arg;
        const children = req?.childrenAges?.length ?? 0;
        state.lastSearchPassengerCount = Math.max(
          1,
          (req?.adults ?? 1) + children
        );
        state.lastFlightSearchRequest = req;
      })
      .addCase(flightSearch.rejected, (state) => {
        state.flightSearchStatus = API_RESULT.FAILED;
        state.flightSearchResults = null;
        state.flightSearchReturnCode = null;
        state.lastFlightSearchRequest = null;
      })
      .addCase(pollFlightSearch.pending, (state) => {
        state.pollSearchRequestCount += 1;
      })
      .addCase(pollFlightSearch.fulfilled, (state, action) => {
        state.pollSearchResponseCount += 1;
        state.flightSearchStatus = API_RESULT.SUCCEEDED;
        const incomingWrapper = action.payload.results;
        const previousWrapper = state.flightSearchResults;
        const mergedClickId =
          action.payload.feClickId ??
          incomingWrapper.feClickId ??
          previousWrapper?.feClickId;
        const mergedResults = mergePollSearchResults(
          previousWrapper?.results,
          incomingWrapper.results
        );
        state.flightSearchResults = {
          ...incomingWrapper,
          results: mergedResults,
          sortingOptions: hasSortingOptions(incomingWrapper.sortingOptions)
            ? incomingWrapper.sortingOptions
            : previousWrapper?.sortingOptions ?? incomingWrapper.sortingOptions,
          stats: incomingWrapper.stats ?? previousWrapper?.stats,
          ...(mergedClickId != null && mergedClickId !== ""
            ? { feClickId: mergedClickId }
            : {}),
        };
        state.flightSearchReturnCode =
          normalizeSkyscannerReturnCode(action.payload.returnCode);
      })
      .addCase(pollFlightSearch.rejected, (state) => {
        state.pollSearchResponseCount += 1;
        state.pollIgnoreErrorNonce += 1;
        if (state.pollIgnoreErrorNonce >= 3) {
          state.flightSearchReturnCode = SkyscannerResponseCode.FAILURE;
        }
      });
  },
});

export const { clearPlaces, clearFlightSearch, setSearchDisplayTexts } = flightsSlice.actions;

export default flightsSlice.reducer;
